/**
 * AI generator prices, as the backend reports them in GET /ai-generator/quota
 * (`pricing`, added 2026-10-12). The UI never hard-codes a price any more: it
 * reads this object and falls back to the defaults below when the field is
 * missing (an older backend, an anonymous visitor — /quota needs a token — or
 * a static page that is rendered without a user at all).
 *
 * Plain dependency-free JS so it can be unit tested with plain `node`
 * (see utils/__tests__/aiPricing.test.js) and imported by server components.
 */

// Current tariff — the fallback whenever the server has not answered yet.
// Keep in step with routes/ai-generator.js (GET /quota → pricing) on the backend.
export const AI_PRICING_DEFAULTS = Object.freeze({
  generation: 1,
  icon: 1,
  variants: 1,
  refine_free_per_widget: 3,
  refine: 1,
  restyle: 0,
  badge: null,
  variants_count: 3,
  more_variants: 0,
  // Edit chat (owner, 2026-10-13): a generated widget includes its first
  // edit_included_generated changes; then edit_pack_price credits open a pack
  // of edit_pack_size more (an imported file pays for its first pack).
  // Questions are free.
  edit_pack_size: 10,
  edit_pack_price: 1,
  edit_included_generated: 10,
});

// Shorthands for static copy (FAQ, pricing tables, llms-style texts) that is
// rendered without a /quota response.
export const AI_GENERATION_CREDITS = AI_PRICING_DEFAULTS.generation;
export const AI_FREE_REFINES = AI_PRICING_DEFAULTS.refine_free_per_widget;
export const AI_REFINE_CREDITS = AI_PRICING_DEFAULTS.refine;
export const AI_EDIT_PACK_SIZE = AI_PRICING_DEFAULTS.edit_pack_size;
export const AI_EDIT_PACK_CREDITS = AI_PRICING_DEFAULTS.edit_pack_price;
export const AI_EDITS_INCLUDED = AI_PRICING_DEFAULTS.edit_included_generated;

// Sign-up credits (backend routes/users.js): a new account gets this many
// credits, valid for SIGNUP_CREDITS_DAYS unless the user buys any pack. Since
// 2026-10-12 (owner decision) they are spent on ANYTHING — the converter and
// the AI generator alike; the generator's separate free-trial counter is gone.
export const SIGNUP_CREDITS = 10;
export const SIGNUP_CREDITS_DAYS = 7;

/**
 * "enough for 10 AI widgets or 10 conversions" — what the sign-up credits buy
 * at the current generation price (a conversion download is 1 credit).
 *
 * @param {number} [generationPrice] credits per AI generation
 */
export const signupCreditsReach = (generationPrice = AI_GENERATION_CREDITS) => {
  const widgets = generationPrice > 0
    ? Math.floor(SIGNUP_CREDITS / generationPrice) : SIGNUP_CREDITS;
  return `enough for ${widgets} AI ${widgets === 1 ? 'widget' : 'widgets'} or ${SIGNUP_CREDITS} conversions`;
};

/** "10 free credits on us, no card" — the one guest-facing wording. */
export const signupOfferText = () => `${SIGNUP_CREDITS} free credits on us, no card`;

const NUMERIC_KEYS = Object.keys(AI_PRICING_DEFAULTS).filter((k) => k !== 'badge');
const MAX_BADGE_LENGTH = 40;
const CYRILLIC_RE = /[\u0400-\u04FF]/;

const isPrice = (v) => typeof v === 'number' && Number.isFinite(v) && v >= 0;

/**
 * Merge a (possibly partial or absent) `pricing` object over the defaults.
 * Anything that is not a non-negative finite number is ignored, so a
 * malformed field can never show "NaN credits" or unlock a gate.
 *
 * @param {object|null|undefined} pricing GET /quota → pricing
 * @returns {typeof AI_PRICING_DEFAULTS}
 */
export function normalizeAiPricing(pricing) {
  const src = pricing && typeof pricing === 'object' ? pricing : {};
  const out = { ...AI_PRICING_DEFAULTS };
  NUMERIC_KEYS.forEach((key) => {
    if (isPrice(src[key])) out[key] = src[key];
  });
  const badge = typeof src.badge === 'string' ? src.badge.trim() : '';
  // The site is English-only: a badge typed in Russian in the admin
  // (ai_config.generator_price_badge) is dropped rather than shown.
  out.badge = badge && !CYRILLIC_RE.test(badge) ? badge.slice(0, MAX_BADGE_LENGTH) : null;
  return out;
}

/**
 * "1 credit" / "3 credits" / "free".
 *
 * @param {number} n
 * @param {{ free?: string }} [opts] label for zero (default "free")
 */
export function formatCredits(n, { free = 'free' } = {}) {
  const value = isPrice(n) ? n : 0;
  if (value === 0) return free;
  return `${value} ${value === 1 ? 'credit' : 'credits'}`;
}
